import React from 'react';
import { Flame, Package } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import { cn } from '../../../utils/cn';

interface Props {
  mode: 'roasting' | 'finished_good';
  onChange: (next: 'roasting' | 'finished_good') => void;
  /**
   * Spec 100: hide tabs the user cannot read. When both flags default to
   * true (legacy usage) the toggle renders as before.
   */
  canShowRoasting?: boolean;
  canShowPacking?: boolean;
}

export const WorkOrdersModeToggle: React.FC<Props> = ({
  mode,
  onChange,
  canShowRoasting = true,
  canShowPacking = true,
}) => {
  const { t } = useLanguage();

  // If only one tab is visible, suppress the toggle entirely — the page is
  // implicitly fixed to that mode.
  if (!canShowRoasting || !canShowPacking) {
    return null;
  }

  return (
    <div
      role="tablist"
      aria-label={t('workOrders.page.mode')}
      className="relative inline-flex w-full sm:w-auto rounded-lg bg-gray-100 p-1 ring-1 ring-inset ring-gray-200/80"
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-md bg-primary-500 shadow-sm ring-1 ring-inset ring-primary-600/20 transition-transform duration-200 ease-out',
          mode === 'finished_good' && 'translate-x-full'
        )}
      />

      <button
        type="button"
        role="tab"
        aria-selected={mode === 'roasting'}
        onClick={() => onChange('roasting')}
        className={cn(
          'relative z-10 flex-1 sm:min-w-[148px] inline-flex items-center justify-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-md transition-colors',
          mode === 'roasting' ? 'text-white' : 'text-gray-500 hover:text-gray-800'
        )}
        data-test-id="wo-mode-roasting"
      >
        <Flame className="h-4 w-4" />
        {t('workOrders.page.roasting')}
      </button>

      <button
        type="button"
        role="tab"
        aria-selected={mode === 'finished_good'}
        onClick={() => onChange('finished_good')}
        className={cn(
          'relative z-10 flex-1 sm:min-w-[148px] inline-flex items-center justify-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-md transition-colors',
          mode === 'finished_good' ? 'text-white' : 'text-gray-500 hover:text-gray-800'
        )}
        data-test-id="wo-mode-finished-good"
      >
        <Package className="h-4 w-4" />
        {t('workOrders.page.packing')}
      </button>
    </div>
  );
};
